/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  fetchAuthStatus,
  signOutUser,
  tryRefreshAuthTokens,
  type AuthStatus,
} from "../../services/authService";
import { fetchAuthSession } from "../../services/cognitoAuth";
import {
  clearAuthCache,
  readAuthCache,
  writeAuthCache,
} from "./authCache";
import {
  registerAuthStatusSetter,
  setupDevSubscriptionHelper,
} from "../../utils/devSubscription";

export type AuthPhase = "restoring" | "signedIn" | "signedOut";
export type ProfilePhase = "idle" | "loading" | "ready" | "error";

interface AuthContextValue {
  authPhase: AuthPhase;
  profilePhase: ProfilePhase;
  authStatus: AuthStatus;
  isAuthenticated: boolean;
  applyAuthStatus: (status: AuthStatus) => void;
  refreshAuth: (options?: { reason?: string; silent?: boolean }) =>
    Promise<AuthStatus>;
  signOut: (options?: { clearStorage?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const cachedStatusRef = useRef<AuthStatus | null | undefined>(undefined);
  if (cachedStatusRef.current === undefined) {
    cachedStatusRef.current = readAuthCache();
  }
  const cachedStatus = cachedStatusRef.current ?? null;

  const [authStatus, setAuthStatus] = useState<AuthStatus>(
    cachedStatus ?? { isAuthenticated: false },
  );
  const [authPhase, setAuthPhase] = useState<AuthPhase>("restoring");
  const [profilePhase, setProfilePhase] = useState<ProfilePhase>(
    cachedStatus?.profile ? "ready" : "idle",
  );

  const authStatusRef = useRef(authStatus);
  useEffect(() => {
    authStatusRef.current = authStatus;
  }, [authStatus]);

  const applyAuthStatus = useCallback(
    (status: AuthStatus, options?: { profilePhase?: ProfilePhase }) => {
      setAuthStatus(status);
      setAuthPhase(status.isAuthenticated ? "signedIn" : "signedOut");
      if (!status.isAuthenticated) {
        setProfilePhase("idle");
        return;
      }
      if (options?.profilePhase) {
        setProfilePhase(options.profilePhase);
        return;
      }
      setProfilePhase(status.profile ? "ready" : "error");
    },
    [],
  );

  const mergeProfileFallback = useCallback((status: AuthStatus): AuthStatus => {
    if (!status.isAuthenticated || status.profile) return status;
    const fallback = authStatusRef.current.profile ?? null;
    if (!fallback) return status;
    return { ...status, profile: fallback };
  }, []);

  const refreshAuth = useCallback(
    async (options?: { reason?: string; silent?: boolean }) => {
      const started = performance.now();
      if (!options?.silent && authStatus.isAuthenticated && !authStatus.profile) {
        setProfilePhase("loading");
      }
      const status = await fetchAuthStatus();
      const mergedStatus = mergeProfileFallback(status);
      applyAuthStatus(mergedStatus, {
        profilePhase: mergedStatus.profile ? "ready" : "error",
      });
      if (import.meta.env.DEV) {
        const elapsed = performance.now() - started;
        console.log("[auth] refresh", {
          ms: Math.round(elapsed),
          reason: options?.reason ?? "manual",
        });
      }
      return mergedStatus;
    },
    [applyAuthStatus, authStatus.isAuthenticated, authStatus.profile, mergeProfileFallback],
  );

  const signOut = useCallback(
    async (options?: { clearStorage?: boolean }) => {
      try {
        await signOutUser();
      } catch {
        // Best-effort sign-out; ignore errors to avoid trapping the user.
      } finally {
        if (options?.clearStorage) {
          try {
            localStorage.clear();
          } catch {
            // Ignore storage errors (e.g. disabled storage in private mode).
          }
        }
        clearAuthCache();
        applyAuthStatus({ isAuthenticated: false });
        if (options?.clearStorage) {
          window.location.reload();
        }
      }
    },
    [applyAuthStatus],
  );

  useEffect(() => {
    registerAuthStatusSetter((next) => {
      setAuthStatus((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        setAuthPhase(resolved.isAuthenticated ? "signedIn" : "signedOut");
        setProfilePhase(
          resolved.isAuthenticated
            ? resolved.profile
              ? "ready"
              : "error"
            : "idle",
        );
        return resolved;
      });
    });
    setupDevSubscriptionHelper();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      const start = performance.now();
      const sessionStart = performance.now();
      let session: Awaited<ReturnType<typeof fetchAuthSession>> | undefined;

      try {
        await tryRefreshAuthTokens();
        session = await fetchAuthSession();
      } catch {
        session = undefined;
      }

      const sessionMs = performance.now() - sessionStart;

      if (cancelled) return;

      const hasToken = !!session?.tokens?.accessToken;
      if (!hasToken) {
        applyAuthStatus({ isAuthenticated: false });
        if (import.meta.env.DEV) {
          console.log("[auth] restore", {
            sessionMs: Math.round(sessionMs),
            profileMs: 0,
            totalMs: Math.round(performance.now() - start),
            authenticated: false,
          });
        }
        return;
      }

      setAuthPhase("signedIn");
      setAuthStatus((prev) => ({ ...prev, isAuthenticated: true }));
      setProfilePhase((prev) => (prev === "ready" ? "ready" : "loading"));

      const profileStart = performance.now();
      const status = await fetchAuthStatus({ session });
      const profileMs = performance.now() - profileStart;

      if (cancelled) return;

      const mergedStatus = mergeProfileFallback(status);
      applyAuthStatus(mergedStatus, {
        profilePhase: mergedStatus.profile ? "ready" : "error",
      });

      if (import.meta.env.DEV) {
        console.log("[auth] restore", {
          sessionMs: Math.round(sessionMs),
          profileMs: Math.round(profileMs),
          totalMs: Math.round(performance.now() - start),
          authenticated: mergedStatus.isAuthenticated,
        });
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
  }, [applyAuthStatus, mergeProfileFallback]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("auth-status-updated", { detail: authStatus }),
    );
  }, [authStatus]);

  useEffect(() => {
    if (authPhase === "restoring") return;
    if (!authStatus.isAuthenticated) {
      clearAuthCache();
      return;
    }
    if (authStatus.profile) {
      writeAuthCache(authStatus);
    }
  }, [authPhase, authStatus]);

  const value = useMemo<AuthContextValue>(
    () => ({
      authPhase,
      profilePhase,
      authStatus,
      isAuthenticated: authStatus.isAuthenticated,
      applyAuthStatus,
      refreshAuth,
      signOut,
    }),
    [
      authPhase,
      profilePhase,
      authStatus,
      applyAuthStatus,
      refreshAuth,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
